import { ListMusic } from "lucide-react";
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DateField,
  Edit,
  FunctionField,
  List,
  NumberField,
  SimpleForm,
  TextField,
  TextInput,
  UrlField,
  required,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { CardListActions, EditorToolbar } from "../../components";

const playlistUrl = (id) => `https://www.torahanytime.com/playlists/${id}`;

const filters = [
  <TextInput source="id" alwaysOn />,
  <TextInput label="Name" source="name" alwaysOn />,
  <TextInput label="Owner user ID" source="user_id" alwaysOn />,
];

function renderForm() {
  return (
    <SimpleForm toolbar={<EditorToolbar />}>
      <TextInput source="name" label="Name" fullWidth validate={required()} />
      <TextInput
        source="user_id"
        label="Owner user ID"
        helperText="Optional. Defaults to the logged-in admin user when creating."
      />
      <BooleanInput
        source="is_public"
        label="Public playlist / shareable URL"
        defaultValue={true}
      />
      <TextInput
        source="lecture_ids_text"
        label="Lecture IDs"
        helperText="Paste IDs separated by new lines, commas, spaces, or a spreadsheet column. Private and undiscoverable lectures are allowed."
        multiline
        fullWidth
        minRows={10}
        validate={required()}
      />
      <FunctionField
        label="Share URL"
        render={(record) =>
          record?.id ? (
            <a href={playlistUrl(record.id)} target="_blank" rel="noreferrer">
              {playlistUrl(record.id)}
            </a>
          ) : (
            "Save to generate a playlist URL"
          )
        }
      />
    </SimpleForm>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: (data) => {
          const id = data?.id || data?.data?.id;
          notify(
            id ? `Playlist created: ${playlistUrl(id)}` : "Playlist created",
            {
              type: "success",
            },
          );
          if (id) {
            redirect("edit", "playlists", id);
          }
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderForm()}
    </Create>
  );
}

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Playlist updated successfully!", { type: "success" });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderForm()}
    </Edit>
  );
}

function ListView(props) {
  return (
    <List
      {...props}
      actions={<CardListActions exporter={true} />}
      filters={filters}
      perPage={25}
      sort={{ field: "updated_at", order: "DESC" }}
      title="Playlists"
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="id" label="ID" />
        <TextField source="name" label="Name" />
        <TextField source="user_id" label="Owner user ID" />
        <NumberField source="lecture_count" label="Lectures" />
        <BooleanField source="is_public" label="Public" />
        <DateField source="updated_at" showTime />
        <UrlField
          source="url"
          label="Share URL"
          target="_blank"
          rel="noreferrer"
        />
      </Datagrid>
    </List>
  );
}

export const Playlists = {
  category: "database",
  name: "playlists",
  create: CreateView,
  edit: EditView,
  icon: ListMusic,
  list: ListView,
  options: { label: "Playlists" },
};
